import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';

const productSchema = Yup.object().shape({
  product: Yup.object().required('Product is required'),
  unit: Yup.string(),
  quantity: Yup.number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .positive('Quantity must be positive'),
  saleRate: Yup.number()
    .typeError('Rate must be a number')
    .required('Rate is required')
    .positive('Rate must be positive'),
  note: Yup.string(),
  customer: Yup.object().required('Customer is required'),
});

const EditSalesOrder = ({ 
  salesOrder, 
  productlist, 
  customersList, 
  onUpdate, 
  onCancel,
  isUpdating,
  isLoading 
}) => {
  // Prepare product options
  const productOptions = productlist?.map(product => ({
    value: product._id,
    label: product.productName,
    unit: product.unit?.unitName || product.unit, // Handle both object and string units
    saleRate: product.saleRate
  })) || [];

  // Prepare customer options
  const customerOptions = customersList?.map(customer => ({
    value: customer._id,
    label: customer.customerName
  })) || [];

  // Find current product and customer
  const currentProduct = productOptions.find(p => 
    p.value === (salesOrder?.product?._id || salesOrder?.product)
  ) || null;

  const currentCustomer = customerOptions.find(c => 
    c.value === (salesOrder?.customer?._id || salesOrder?.customer)
  ) || null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
  saleDate: salesOrder?.saleDate ? new Date(salesOrder.saleDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      product: currentProduct,
      customer: currentCustomer,
      quantity: salesOrder?.quantity || 0,
      unit: salesOrder?.unit || (currentProduct?.unit || ''),
      saleRate: salesOrder?.saleRate || salesOrder?.rate || (currentProduct?.saleRate || ''),
      note: salesOrder?.note || ''
    },
    validationSchema: productSchema,
    onSubmit: (values) => {
      const updateData = {
        _id: salesOrder._id,
        productId: values.product.value,
        customerId: values.customer.value,
        quantity: values.quantity,
        unit: values.unit,
        saleRate: values.saleRate,
        note: values.note,
        saleDate: values.saleDate // already in YYYY-MM-DD format
      };
      onUpdate(updateData);
    }
  });

  const handleProductSelect = (option) => {
    formik.setFieldValue('product', option);
    if (option) {
      formik.setFieldValue('unit', option.unit || '');
      formik.setFieldValue('saleRate', option.saleRate || '');
    }
  };

  const getError = (field) => {
    return formik.touched[field] && formik.errors[field];
  };

  return (
    <Modal show={true} onHide={onCancel} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Sale Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">Loading sale order data...</div>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Sale Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="saleDate"
                    value={formik.values.saleDate ? new Date(formik.values.saleDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => formik.setFieldValue('saleDate', e.target.value)}
                  />
                  {getError('saleDate') && (
                    <div className="text-danger">{getError('saleDate')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Customer Name</label>
                  <Select
                    name="customer"
                    options={customerOptions}
                    classNamePrefix="react-select"
                    placeholder="Choose"
                    value={formik.values.customer}
                    onChange={(option) => formik.setFieldValue('customer', option)}
                    onBlur={() => formik.setFieldTouched('customer', true)}
                  />
                  {getError('customer') && (
                    <div className="text-danger">{getError('customer')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-3 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Product Name</label>
                  <Select
                    name="product"
                    options={productOptions}
                    classNamePrefix="react-select"
                    placeholder="Choose"
                    value={formik.values.product}
                    onChange={handleProductSelect}
                    onBlur={() => formik.setFieldTouched('product', true)}
                  />
                  {getError('product') && (
                    <div className="text-danger">{getError('product')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-2 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    name="quantity"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {getError('quantity') && (
                    <div className="text-danger">{getError('quantity')}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-lg-2 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Unit</label>
                  <input
                    type="text"
                    className="form-control"
                    name="unit"
                    value={formik.values.unit}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly
                  />
                  {getError('unit') && (
                    <div className="text-danger">{getError('unit')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-2 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Sale Rate</label>
                  <input
                    type="number"
                    className="form-control"
                    name="saleRate"
                    value={formik.values.saleRate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {getError('saleRate') && (
                    <div className="text-danger">{getError('saleRate')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-2 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>Note</label>
                  <input
                    type="text"
                    className="form-control"
                    name="note"
                    value={formik.values.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {getError('note') && (
                    <div className="text-danger">{getError('note')}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-2 col-md-6 col-sm-12 d-flex align-items-end">
                  <div className="input-blocks w-100 d-flex justify-content-end ms-5">
                  <button 
                    type="submit" 
                    className="btn btn-success me-2"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditSalesOrder;